import React from "react";
import { useCurrentUserLocale } from "@linkfluence/core/user/use-cases";
import { AbstractDate } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import LimitCard from "@radarly/components/cards/limits";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { Card } from "@radarly/models/cards";
import {
  isAICard,
  isClassificationCard,
  isClusterCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isEmbeddedCard,
  isEmvCard,
  isGeoCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isPivotCard,
  isPostsCard,
  isTermCard,
  isTermMetricsCard,
  isTextCard,
  isUsersCard,
  useCardLimits,
} from "@radarly/models/cards/is";
import { CardFactory } from "@radarly/models/cards/types/base";
import { Clause } from "@radarly/models/clause";
import { Dataviz } from "@radarly/models/dataviz";
import { PostsPanel } from "@radarly/models/panel";
import Subtitle from "@radarly/shared/cards/edition/subtitle";
import AI from "./ai";
import TopAuthors from "./authors";
import Classification from "./classification";
import Clusters from "./clusters";
import DateMetric from "./date-metric";
import DateMetrics from "./date-metrics";
import DateTerm from "./date-term";
import Demographic from "./demographic";
import Embedded from "./embedded";
import Emv from "./emv";
import Geo from "./geo";
import PinnedAuthor from "./pinned-author";
import PinnedPost from "./pinned-post";
import Pivot from "./pivot";
import Posts from "./posts";
import Term from "./term";
import TermMetrics from "./term-metrics";
import Text from "./text";

interface IProps {
  card: Card;
  height: number;
  timezone: Timezone;
  forceReload?: boolean;
  clause?: Clause;
  from?: AbstractDate;
  to?: AbstractDate;
  openPostsPanel?: (data: PostsPanel["data"]) => void;
  readOnly?: boolean;
  nowDate?: Date;
  setCard?: (card: Card) => void;
  setDataviz?: (dataviz: Dataviz) => void;
  isEdition?: boolean;
  aiContext?: string;
  isWizard?: boolean;
}

const RadarlyCards = ({ card, ...props }: IProps) => {
  const focuses = useContextualFocuses();
  const limitMsg = useCardLimits(card, focuses);
  const locale = useCurrentUserLocale();

  return limitMsg.length > 0 ? (
    <LimitCard
      title={card.title}
      message={limitMsg}
      locale={locale}
      {...props}
      subtitle={
        <Subtitle
          timezone={props.timezone}
          fragments={card.fragments}
          dataviz={card.dataviz}
        />
      }
    />
  ) : isDateMetricCard(card) ? (
    <DateMetric card={card} {...props} />
  ) : isDateTermCard(card) ? (
    <DateTerm card={card} {...props} />
  ) : isDemographicCard(card) ? (
    <Demographic card={card} {...props} />
  ) : isUsersCard(card) ? (
    <TopAuthors card={card} {...props} />
  ) : isPostsCard(card) ? (
    <Posts card={card} {...props} />
  ) : isClusterCard(card) ? (
    <Clusters card={card} {...props} />
  ) : isDateMetricsCard(card) ? (
    <DateMetrics card={card} {...props} />
  ) : isTermMetricsCard(card) ? (
    <TermMetrics card={card} {...props} />
  ) : isPivotCard(card) ? (
    <Pivot card={card} {...props} />
  ) : isGeoCard(card) ? (
    <Geo card={card} {...props} />
  ) : isTermCard(card) ? (
    <Term card={card} {...props} />
  ) : isClassificationCard(card) ? (
    <Classification card={card} {...props} />
  ) : isEmbeddedCard(card) ? (
    <Embedded card={card} {...props} />
  ) : isTextCard(card) ? (
    <Text card={card} {...props} />
  ) : isPinnedPostCard(card) ? (
    <PinnedPost card={card} {...props} />
  ) : isPinnedAuthorCard(card) ? (
    <PinnedAuthor card={card} {...props} />
  ) : isEmvCard(card) ? (
    <Emv card={card} {...props} />
  ) : isAICard(card) ? (
    <AI
      card={CardFactory.getInstance().addIPAiContextToCard(
        card,
        props?.aiContext
      )}
      {...props}
    />
  ) : (
    <div>other card</div>
  );
};

export default RadarlyCards;
